import { LegacyCookieKeys } from './constants';

/**
 * Cookie cleanup utilities for the legacy (pre-Cognito) auth scheme.
 *
 * Context: before the Cognito migration, `authToken` and `authAccessToken`
 * were set on `.rentgrata.com` (via ari/manager/web). Post-migration the
 * Cognito flow writes to distinct cookie names (`cognitoIdToken`,
 * `cognitoAccessToken`), but legacy cookies may still be sitting in the
 * browser and, because they share neither exact name nor value shape with
 * the new scheme, cause confusion for any reader that walks cookies loosely
 * or for downstream apps that haven't been updated yet.
 *
 * These helpers wipe every plausible (name, domain, path) variant of the
 * legacy auth cookies. They are idempotent: running them when no legacy
 * cookie is present is a no-op.
 */

const EXPIRED = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Expire a cookie across every (domain, path) combination the browser might
 * have it stored under. Needed because a cookie is only deleted when the
 * `Set-Cookie` that expires it has matching `domain` and `path` attributes;
 * we don't know which combo a legacy writer used, so we try all plausible
 * ones.
 */
function expireCookieEverywhere(name: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  const host = window.location.hostname;
  const domainCandidates = new Set<string | undefined>([undefined, host]);

  const parts = host.split('.');
  for (let i = 1; i < parts.length; i += 1) {
    const parent = parts.slice(i).join('.');
    if (parent.includes('.')) {
      domainCandidates.add(parent);
      domainCandidates.add(`.${parent}`);
    }
  }

  const paths = ['/', ''];

  domainCandidates.forEach((domain) => {
    paths.forEach((path) => {
      const domainAttr = domain ? `; domain=${domain}` : '';
      const pathAttr = path ? `; path=${path}` : '';
      document.cookie = `${name}=; ${EXPIRED}${pathAttr}${domainAttr}`;
    });
  });
}

export function cleanupLegacyAuthCookies(): string[] {
  if (typeof document === 'undefined') return [];

  const targets = [
    LegacyCookieKeys.Auth,
    LegacyCookieKeys.AuthAccessToken,
  ];

  const present = new Set(
    document.cookie
      .split(';')
      .map((entry) => entry.trim().split('=')[0])
      .filter(Boolean),
  );

  const cleared: string[] = [];
  targets.forEach((name) => {
    if (present.has(name)) cleared.push(name);
    expireCookieEverywhere(name);
  });

  return cleared;
}
